import type { StorageResourceProperties, StorageResourceRef } from '../../../../types/azureStorage';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import { formatNumericDisplay } from '../../common/utils/columnAlignment';
import { blobTypeLabel, formatStorageSize } from '../utils/storageItemRows';
import { formatStorageDateTime, storageDateTimeUtc } from '../utils/storageDateTime';

type Props = {
  resource: Extract<StorageResourceRef, { kind: 'blob' | 'container' }>;
  value: StorageResourceProperties;
  onChangeTier?: () => void;
  readOnly?: boolean;
};

const text = (value: unknown): string => value === undefined || value === null ? '—' : String(value);

/** UI review F11: the grid's size formatter ("792 KB"), with the exact byte count beside it and in the tooltip. */
export function sizeText(raw: unknown): { text: string; title: string } {
  const bytes = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\d+$/u.test(raw) ? Number(raw) : undefined;
  if (bytes === undefined || !Number.isFinite(bytes)) return { text: text(raw), title: text(raw) };
  const exact = `${formatNumericDisplay(bytes) ?? bytes} bytes`;
  return { text: bytes < 1024 ? formatStorageSize(bytes) : `${formatStorageSize(bytes)} (${exact})`, title: exact };
}

export function PropertiesSystemSection({ resource, value, onChangeTier, readOnly }: Props) {
  const properties = value.properties;
  const tier = text(properties.accessTier ?? properties.tier);
  const archive = typeof properties.archiveStatus === 'string' ? ` · ${properties.archiveStatus}` : '';
  const rows: Array<[string, unknown]> = resource.kind === 'blob'
    ? [
        ['Size', properties.contentLength ?? properties.size],
        // C1 (N11 / A19): "Block blob", as the grid says it — not the service's `BlockBlob`.
        ['Type', typeof properties.blobType === 'string' ? blobTypeLabel(properties.blobType) : properties.contentType],
        ['Tier', `${tier}${archive}`],
        ['ETag', properties.etag],
        ['Last modified', properties.lastModified],
        ['Lease state', properties.leaseState],
      ]
    : [
        ['ETag', properties.etag],
        ['Last modified', properties.lastModified],
        ['Lease state', properties.leaseState],
      ];
  return (
    <section className="space-y-2" data-testid="storage-properties-system">
      <h3 className="text-sm font-medium">System properties</h3>
      <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        {rows.map(([label, raw]) => {
          // U11: dates in the one storage format, the exact UTC instant as the tooltip.
          const shown = label === 'Size' ? sizeText(raw) : label === 'Last modified' && raw ? { text: formatStorageDateTime(raw as string), title: storageDateTimeUtc(raw as string) ?? text(raw) } : { text: text(raw), title: text(raw) };
          return (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="truncate" title={shown.title} data-testid={`storage-properties-system-${label.toLowerCase().replace(/\s+/gu, '-')}`}>{shown.text}</dd>
            </div>
          );
        })}
      </dl>
      {resource.kind === 'blob' && onChangeTier && (
        // R8 (C1): unavailable with the reason a keyboard user reaches, not a native disabled button with a title.
        <ReasonButton type="button" variant="outline" size="xs" reason={readOnly ? STORAGE_READ_ONLY_REASON : undefined} onClick={onChangeTier} data-testid="storage-properties-change-tier">Change tier…</ReasonButton>
      )}
    </section>
  );
}
